import React, { useState } from "react";
import { MdAttachFile } from "react-icons/md";
import { FiCamera, FiSend, FiX } from "react-icons/fi";
import imageCompression from "browser-image-compression";
import heic2any from "heic2any";

const ChatInputBox = ({
  onSend,
  enableCamera = true,
  enableAttachFile = true,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [attachedImage, setAttachedImage] = useState(null); // State to hold the attached image

  // Function to handle sending the message
  const handleSend = () => {
    if (inputValue.trim() || attachedImage) {
      // Send the message and/or image
      onSend({ text: inputValue, image: attachedImage });
      setInputValue("");
      setAttachedImage(null); // Clear the attached image after sending
    }
  };

  // Function to compress the image
  const compressImage = async (file) => {
    try {
      let options = {
        maxSizeMB: 0.1, // Initial maximum size in MB (100 KB = 0.1 MB)
        maxWidthOrHeight: 1024, // Resize image dimensions if larger
        useWebWorker: true, // Use a web worker for performance
      };

      let compressedFile = await imageCompression(file, options);

      // If the compressed size is still larger than 150 KB, compress further
      while (compressedFile.size > 100 * 1024 && options.maxSizeMB > 0.01) {
        options.maxSizeMB -= 0.01; // Gradually reduce the max size limit
        compressedFile = await imageCompression(file, options);
      }

      console.log(
        `Original size: ${(file.size / 1024).toFixed(
          2
        )} KB, Compressed size: ${(compressedFile.size / 1024).toFixed(2)} KB`
      );

      return compressedFile;
    } catch (error) {
      console.error("Error compressing image:", error);
      return file; // Return the original file if compression fails
    }
  };

  // Function to handle capturing the photo
  const handleFileChange = async (event) => {
    let file = event.target.files[0];
    if (file) {
      // Check for HEIC file
      if (
        file.type === "image/heic" ||
        file.name.toLowerCase().endsWith(".heic")
      ) {
        try {
          const convertedBlob = await heic2any({
            blob: file,
            toType: "image/jpeg", // Convert to JPEG
          });
          const convertedFile = new File([convertedBlob], "converted.jpeg", {
            type: "image/jpeg",
          });
          file = convertedFile; // Replace the file with the converted one
        } catch (error) {
          console.error("Error converting HEIC file:", error);
          return;
        }
      }
      // Compress the image using browser-image-compression
      const compressedFile = await compressImage(file);
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target.result;

        img.onload = () => {
          // Create a canvas to convert the image
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);

          // Convert the canvas content to a PNG data URL
          const imageURL = canvas.toDataURL("image/png");
          setAttachedImage(imageURL); // Attach the PNG-formatted image
        };
      };

      reader.readAsDataURL(compressedFile); // Read the uploaded image as a data URL
      // Reset file input value to allow re-uploading the same file
      event.target.value = null;
    }
  };

  // Function to remove the attached image
  const handleRemoveImage = () => {
    setAttachedImage(null);
  };

  return (
    <div className="flex flex-col items-center bg-gray-800 px-2 py-2 shadow-lg ml-4 mr-4 mb-4 rounded-3xl">
      {/* Attached Image Preview */}
      {attachedImage && (
        <div className="flex items-start w-full px-2 py-2 rounded-lg">
          <div className="relative w-16 h-16 flex-shrink-0 mr-2">
            <img
              src={attachedImage}
              alt="Attachment"
              className="w-full h-full rounded-md object-cover"
            />
            <button
              onClick={handleRemoveImage}
              className="absolute top-0 right-0 bg-black bg-opacity-50 text-white rounded-full p-1"
            >
              <FiX size={12} />
            </button>
          </div>
        </div>
      )}

      {/* Text Input */}
      <div className="flex flex-col w-full">
        <input
          type="text"
          placeholder="Message Fiducia"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          className="min-h-[44px] bg-transparent text-gray-200 placeholder-gray-500 w-full px-2 focus:outline-none"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            } 
          }}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex flex-row justify-between items-center px-2 h-[44px] w-full">
        <div className="flex flex-row items-center px-2 space-x-3">
          {/* <button className="text-gray-400 hover:text-gray-200">
            <MdAttachFile size={24} className="transform -scale-x-100" />
          </button> */}
          {/* Attachment Icon */}
          <label>
            <input
              type="file"
              accept="image/*,.heic,.HEIC"
              onChange={handleFileChange}
              style={{ display: "none" }} // Hidden input
            />
            {enableAttachFile ? (
              <div className="text-gray-400 hover:text-gray-200 cursor-pointer">
                <MdAttachFile size={24} className="transform -scale-x-100" />
              </div>
            ) : (
              <></>
            )}
          </label>
          {/* Camera Icon */}
          <label>
            <input
              type="file"
              accept="image/*,.heic,.HEIC"
              capture="environment"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            {enableCamera ? (
              <div className="text-gray-400 hover:text-gray-200 cursor-pointer">
                <FiCamera size={24} />
              </div>
            ) : (
              <></>
            )}
          </label>
        </div>
        <div>
          {/* Send Button */}
          <button
            onClick={handleSend}
            className="text-gray-400 hover:text-gray-200 p-1 rounded-full"
          >
            <FiSend size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInputBox;
